import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip,
  InputAdornment, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Search, Visibility, Delete, Storage, Edit, Check, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function DemDatabases() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [databases, setDatabases] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchDb, setSearchDb] = useState('');
  const [searchPartner, setSearchPartner] = useState('');
  const [editingDbId, setEditingDbId] = useState(null);
  const [editMaildomeId, setEditMaildomeId] = useState('');

  useEffect(() => { fetchDatabases(); }, []);

  const fetchDatabases = async () => {
    try { const res = await axios.get('/admin/dem/databases'); setDatabases(res.data.data || []); }
    catch (error) { console.error('Error fetching databases:', error); }
    finally { setLoading(false); }
  };

  const fetchAvailablePartners = async () => {
    try { const res = await axios.get('/admin/dem/databases/available-partners'); setAvailablePartners(res.data.data || []); }
    catch (error) { console.error('Error fetching partners:', error); }
  };

  const handleOpenAddDialog = () => { fetchAvailablePartners(); setSearchPartner(''); setAddDialogOpen(true); };

  const handleAddDatabase = async (partnerId, partnerName) => {
    try {
      await axios.post('/admin/dem/databases', { partner_id: partnerId, name: partnerName });
      setMessage({ type: 'success', text: 'Database added successfully' });
      setAddDialogOpen(false);
      fetchDatabases();
    } catch (error) { setMessage({ type: 'error', text: error.response?.data?.error || 'Error adding database' }); }
  };

  const handleDeleteDatabase = async (id) => {
    if (!window.confirm('Are you sure you want to delete this database?')) return;
    try {
      await axios.delete(`/admin/dem/databases/${id}`);
      setMessage({ type: 'success', text: 'Database deleted' });
      fetchDatabases();
    } catch (error) { setMessage({ type: 'error', text: 'Error deleting database' }); }
  };

  const handleStartEditMaildomeId = (db) => { setEditingDbId(db.id); setEditMaildomeId(db.maildome_db_id || ''); };

  const handleSaveMaildomeId = async (dbId) => {
    try {
      await axios.put(`/admin/dem/databases/${dbId}/maildome`, { maildome_db_id: editMaildomeId ? parseInt(editMaildomeId) : null });
      setEditingDbId(null);
      fetchDatabases();
      setMessage({ type: 'success', text: 'Maildome ID saved' });
    } catch (error) { setMessage({ type: 'error', text: 'Error saving Maildome ID' }); }
  };

  const handleCancelEdit = () => { setEditingDbId(null); setEditMaildomeId(''); };

  const filteredDatabases = databases.filter(db => searchDb === '' || db.name?.toLowerCase().includes(searchDb.toLowerCase()) || db.partner_name?.toLowerCase().includes(searchDb.toLowerCase()));
  const filteredPartners = availablePartners.filter(p => searchPartner === '' || p.name?.toLowerCase().includes(searchPartner.toLowerCase()) || p.company?.toLowerCase().includes(searchPartner.toLowerCase()));

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>DEM Databases</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenAddDialog} size={isMobile ? 'small' : 'medium'}>
          {isMobile ? 'Add' : 'Add Database'}
        </Button>
      </Box>

      {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 } }}>
          <TextField size="small" placeholder="Search databases..." value={searchDb} onChange={(e) => setSearchDb(e.target.value)} sx={{ width: { xs: '100%', sm: 300 } }} InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> }} />
        </CardContent>
      </Card>

      <Card>
        <TableContainer sx={{ maxHeight: { xs: 400, sm: 600 } }}>
          <Table size={isMobile ? 'small' : 'medium'} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                {!isMobile && <TableCell align="center">Maildome ID</TableCell>}
                <TableCell align="center">VL</TableCell>
                {!isMobile && <TableCell align="center">Status</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDatabases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isMobile ? 3 : 5} align="center" sx={{ py: 4 }}>
                    <Storage sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>No databases configured</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredDatabases.map((db) => (
                <TableRow key={db.id} hover>
                  <TableCell onClick={() => navigate(`/dem/databases/${db.id}`)} sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}>
                    <Typography fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{db.name}</Typography>
                    {isMobile && (
                      <Box display="flex" gap={0.5} mt={0.5}>
                        <Chip size="small" label={db.maildome_db_id || 'No ID'} color={db.maildome_db_id ? 'primary' : 'default'} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                        <Chip size="small" label={db.status === 'active' ? 'Active' : 'Off'} color={db.status === 'active' ? 'success' : 'default'} sx={{ fontSize: '0.6rem', height: 18, color: 'white' }} />
                      </Box>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell align="center">
                      {editingDbId === db.id ? (
                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                          <TextField size="small" type="number" value={editMaildomeId} onChange={(e) => setEditMaildomeId(e.target.value)} placeholder="ID" sx={{ width: 70 }} inputProps={{ style: { textAlign: 'center', padding: '4px 8px' } }} onKeyPress={(e) => e.key === 'Enter' && handleSaveMaildomeId(db.id)} autoFocus />
                          <IconButton size="small" color="success" onClick={() => handleSaveMaildomeId(db.id)}><Check fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={handleCancelEdit}><Close fontSize="small" /></IconButton>
                        </Box>
                      ) : (
                        <Chip label={db.maildome_db_id || 'Not set'} size="small" color={db.maildome_db_id ? 'primary' : 'default'} variant={db.maildome_db_id ? 'filled' : 'outlined'} onClick={() => handleStartEditMaildomeId(db)} icon={<Edit sx={{ fontSize: 12 }} />} sx={{ cursor: 'pointer', fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                  )}
                  <TableCell align="center"><Chip size="small" label={db.vl_count || 0} sx={{ fontSize: '0.7rem', height: 22 }} /></TableCell>
                  {!isMobile && (
                   <TableCell align="center">
                     <Chip size="small" label={db.status === 'active' ? 'Active' : 'Inactive'} color={db.status === 'active' ? 'success' : 'error'} sx={{ fontSize: '0.7rem', color: 'white' }} />
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => navigate(`/dem/databases/${db.id}`)}><Visibility fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteDatabase(db.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add Database Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Add Database from Partner</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" placeholder="Search partners..." value={searchPartner} onChange={(e) => setSearchPartner(e.target.value)} sx={{ my: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20 }} /></InputAdornment> }} />
          <Box sx={{ maxHeight: { xs: 300, sm: 400 }, overflow: 'auto' }}>
            {filteredPartners.length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>No available partners</Typography>
            ) : filteredPartners.map((partner) => (
              <Card key={partner.partner_id} variant="outlined" sx={{ p: 1.5, mb: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }} onClick={() => handleAddDatabase(partner.partner_id, partner.name)}>
                <Typography fontWeight={600} sx={{ fontSize: '0.875rem' }}>{partner.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{partner.company}</Typography>
              </Card>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DemDatabases;
